import React, { useState, useRef, useEffect, useCallback } from "react"
import { getSuggestions, parseTemplateSegments } from "./templateSuggestions"

/**
 * Template-aware text input with syntax highlighting and autocomplete (Liquid syntax)
 *
 * Features:
 * - Highlights {{ ... }} template variables in light blue
 * - Shows autocomplete dropdown when typing {{ with smart suggestions
 * - Supports namespaces: params, details, variables, auth
 * - Supports Liquid filters: date_value, format_date, add_duration, url_encode, etc.
 */
export function TemplateInput({
  value,
  onChange,
  placeholder,
  className = "",
  disabled = false,
  id
}) {
  const [ showSuggestions, setShowSuggestions ] = useState(false)
  const [ suggestions, setSuggestions ] = useState([])
  const [ replaceRange, setReplaceRange ] = useState({ start: 0, end: 0 })
  const [ selectedIndex, setSelectedIndex ] = useState(0)
  const inputRef = useRef(null)
  const backdropRef = useRef(null)

  // Shared text styles that MUST match between input and backdrop
  const sharedTextStyles = {
    fontFamily: "inherit",
    fontSize: "14px",
    lineHeight: "1.5",
    padding: "8px 12px",
    border: "1px solid transparent",
    whiteSpace: "pre"
  }

  // Update suggestions when cursor moves or text changes
  const updateSuggestions = useCallback(() => {
    if (!inputRef.current) return

    const cursorPos = inputRef.current.selectionStart || 0
    const { suggestions: newSuggestions, replaceStart, replaceEnd } = getSuggestions(value || "", cursorPos)

    setSuggestions(newSuggestions)
    setReplaceRange({ start: replaceStart, end: replaceEnd })
    setShowSuggestions(newSuggestions.length > 0)
    setSelectedIndex(0)
  }, [ value ])

  // Handle input changes
  function handleChange(e) {
    onChange(e.target.value)
  }

  // Handle keyboard navigation in suggestions
  function handleKeyDown(e) {
    if (!showSuggestions || suggestions.length === 0) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % suggestions.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length)
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (showSuggestions && suggestions[selectedIndex]) {
        e.preventDefault()
        selectSuggestion(suggestions[selectedIndex])
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
    }
  }

  // Select a suggestion and insert it
  function selectSuggestion(suggestion) {
    const before = (value || "").slice(0, replaceRange.start)
    const after = (value || "").slice(replaceRange.end)

    // Determine what to insert based on suggestion type
    let insertValue
    let cursorOffset = 0

    if (suggestion.isNamespace) {
      // Namespace: insert prefix, cursor stays at end
      insertValue = suggestion.value
    } else if (suggestion.isFilter) {
      // Filter: insert filter, cursor after it (allow chaining more filters)
      insertValue = suggestion.value
    } else {
      // Variable: insert value with closing }}, cursor before }}
      insertValue = suggestion.value + " }}"
      cursorOffset = 3
    }

    const newValue = before + insertValue + after
    onChange(newValue)
    setShowSuggestions(false)

    // Move cursor appropriately
    setTimeout(() => {
      if (inputRef.current) {
        const newPos = before.length + insertValue.length - cursorOffset
        inputRef.current.setSelectionRange(newPos, newPos)
        inputRef.current.focus()
      }
    }, 0)
  }

  // Sync scroll between input and backdrop
  function handleScroll() {
    if (backdropRef.current && inputRef.current) {
      backdropRef.current.scrollLeft = inputRef.current.scrollLeft
    }
  }

  // Update suggestions on cursor movement
  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    function handleSelectionChange() {
      updateSuggestions()
    }

    document.addEventListener("selectionchange", handleSelectionChange)

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange)
    }
  }, [ updateSuggestions ])

  // Update suggestions when value changes
  useEffect(() => {
    updateSuggestions()
  }, [ value, updateSuggestions ])

  // Render highlighted segments
  const segments = parseTemplateSegments(value || "")

  return (
    <div className={`relative ${className}`}>
      {/* Backdrop layer - shows highlights behind the input */}
      <div
        ref={backdropRef}
        className="absolute inset-0 pointer-events-none overflow-hidden rounded-md"
        aria-hidden="true"
        style={sharedTextStyles}
      >
        {segments.map((seg, i) =>
          seg.isTemplate ? (
            <span
              key={i}
              className="bg-sky-200 dark:bg-sky-800 rounded-sm text-transparent"
            >
              {seg.text}
            </span>
          ) : (
            <span key={i} className="text-transparent">{seg.text}</span>
          )
        )}
      </div>

      {/* Actual input - sits on top with transparent background */}
      <input
        ref={inputRef}
        type="text"
        id={id}
        value={value || ""}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onScroll={handleScroll}
        onClick={updateSuggestions}
        placeholder={placeholder}
        disabled={disabled}
        className="relative block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent dark:text-gray-100"
        style={sharedTextStyles}
        autoComplete="off"
      />

      {/* Autocomplete dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <ul className="py-1">
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.value}
                onClick={() => selectSuggestion(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`
                  cursor-pointer px-3 py-2 text-sm
                  ${index === selectedIndex
                    ? "bg-blue-50 dark:bg-blue-900/30"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <code className={`font-mono text-xs ${suggestion.color || "text-blue-600 dark:text-blue-400"}`}>
                    {suggestion.isFilter
                      ? `|${suggestion.value}`
                      : suggestion.isNamespace
                        ? `{{ ${suggestion.value}`
                        : `{{ ${suggestion.value} }}`}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 truncate max-w-[200px]">
                    {suggestion.description}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default TemplateInput
